/**
 * AccessoryBar — モバイルのキーボード上部に常設するボタン列（docs/03-editor.md §3）。
 *
 * この実装が守っている IME 規律:
 * 1. **ボタンはフォーカスを奪わない。** `onPointerDown` で `preventDefault()` する。
 *    フォーカスが textarea / CM6 から外れると composition が中断されるため。
 * 2. **変換中にボタンが押されたら何もしない。** 確定を待って実行もしない
 *    （誤爆防止を優先。docs/03 §4-2）。判定は `adapter.isComposing()` のみ。
 * 3. adapter は ref 経由で受け取り、押下時に読む。adapter の差し替え（エディタ切り替え）
 *    でこのコンポーネントが再描画されないようにするため。
 * 4. `visualViewport` の追従は **DOM のスタイル直接書き換え**で行い、React の再描画を
 *    起こさない。変換中に再描画すると composition が壊れるため（docs/03 §4-4）。
 * 5. 対象は Android Chrome。iOS 互換ハックは書かない（CLAUDE.md「やらないこと」）。
 */

import { memo, useCallback, useEffect, useRef, type RefObject, type PointerEvent } from 'react';
import {
  BOLD_MARKER,
  CODE_MARKER,
  ITALIC_MARKER,
  cycleHeading,
  insertAtSelection,
  insertHorizontalRule,
  toggleBulletList,
  toggleCodeFence,
  toggleInlineWrap,
  toggleQuote,
  type TextEdit,
  type TextState,
} from '@shared/markdown-input';
import { Icon, type IconName } from '@app/components/icons';
import type { EditorAdapter } from './EditorAdapter';
import './editor.css';

export interface AccessoryBarProps {
  /** 現在のエディタの adapter を保持する ref（未接続時は null）。 */
  adapterRef: RefObject<EditorAdapter | null>;
  /** 画像追加ボタン。実際のアップロード処理は呼び出し側が繋ぐ。 */
  onRequestImage?: () => void;
  /**
   * 切り出しボタン。選択範囲を渡すので、呼び出し側がサーバーへ依頼する。
   * 選択が空なら `null` を渡す（呼び出し側が案内を出す）。
   */
  onRequestExtract?: (selection: { start: number; end: number } | null) => void;
  /**
   * キーボード追従（`visualViewport`）を行うか。既定 true。
   * 検証ページなどでページ内に固定表示したい場合に false。
   */
  followKeyboard?: boolean;
}

function AccessoryBarImpl({
  adapterRef,
  onRequestImage,
  onRequestExtract,
  followKeyboard = true,
}: AccessoryBarProps) {
  const barRef = useRef<HTMLDivElement | null>(null);
  // onRequestImage は毎描画で参照が変わりうるので ref 経由で最新を読む。
  const onRequestImageRef = useRef(onRequestImage);
  onRequestImageRef.current = onRequestImage;
  const onRequestExtractRef = useRef(onRequestExtract);
  onRequestExtractRef.current = onRequestExtract;

  /**
   * visualViewport に追従してバーを持ち上げる。
   * ソフトキーボードが出ると visualViewport.height が縮み、offsetTop がずれる。
   * `innerHeight - (height + offsetTop)` が「画面下端からキーボード上端までの距離」。
   */
  useEffect(() => {
    if (!followKeyboard) return;
    const vv = window.visualViewport;
    const bar = barRef.current;
    if (!vv || !bar) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const bottomGap = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));
      bar.style.transform = `translateY(${-bottomGap}px)`;
    };
    const schedule = () => {
      // IME 規律: resize/scroll は変換中にも頻繁に来る。React の state を触らず、
      // rAF で間引いて style のみ書き換える。
      if (frame === 0) frame = requestAnimationFrame(update);
    };

    update();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
    };
  }, [followKeyboard]);

  /** IME 規律 1: フォーカスを奪わない。 */
  const handlePointerDown = useCallback((ev: PointerEvent<HTMLButtonElement>) => {
    ev.preventDefault();
  }, []);

  /** IME 規律 2: 変換中は何もしない（キューにも積まない）。 */
  const run = useCallback(
    (action: (adapter: EditorAdapter) => void) => {
      const adapter = adapterRef.current;
      if (!adapter) return;
      if (adapter.isComposing()) return;
      action(adapter);
    },
    [adapterRef],
  );

  /**
   * `markdown-input.ts` の純粋関数を 1 つ適用する。
   * すべてのボタンがこれを通るので、両エディタで挙動が構造的に一致する（docs/03 §1）。
   */
  const apply = useCallback(
    (compute: (state: TextState) => TextEdit | null) =>
      run((a) => {
        const sel = a.getSelection();
        const edit = compute({
          value: a.getValue(),
          selectionStart: sel.start,
          selectionEnd: sel.end,
        });
        if (edit) a.applyEdit(edit);
      }),
    [run],
  );

  const onOutdent = useCallback(() => run((a) => a.indentSelection(-1)), [run]);
  const onIndent = useCallback(() => run((a) => a.indentSelection(1)), [run]);
  const onBullet = useCallback(() => apply(toggleBulletList), [apply]);
  const onHeading = useCallback(() => apply(cycleHeading), [apply]);
  const onQuote = useCallback(() => apply(toggleQuote), [apply]);
  const onBold = useCallback(() => apply((s) => toggleInlineWrap(s, BOLD_MARKER)), [apply]);
  const onItalic = useCallback(() => apply((s) => toggleInlineWrap(s, ITALIC_MARKER)), [apply]);
  const onInlineCode = useCallback(() => apply((s) => toggleInlineWrap(s, CODE_MARKER)), [apply]);
  const onCodeFence = useCallback(() => apply(toggleCodeFence), [apply]);
  const onHorizontalRule = useCallback(() => apply(insertHorizontalRule), [apply]);
  const onImage = useCallback(
    () =>
      run(() => {
        onRequestImageRef.current?.();
      }),
    [run],
  );

  /**
   * 切り出し（docs/01-requirements.md §5.11）。
   *
   * スマホではここが唯一の現実的な動線。ボタンは `pointerdown` でフォーカスを
   * 奪わないので、**選択範囲を保ったまま**押せる。
   */
  const onExtract = useCallback(
    () =>
      run((a) => {
        const sel = a.getSelection();
        onRequestExtractRef.current?.(sel.start === sel.end ? null : sel);
      }),
    [run],
  );

  // 並び順は使用頻度順。バーは横スクロールするので、よく使うものを左に置く。
  // 表示は線画アイコン（ADR-033）。**意味は aria-label / title が持つ**ので、
  // 記号の解読を利用者に強いない（`・—` や `❞` が何を指すかは伝わっていなかった）。
  const buttons: Array<{ icon: IconName; name: string; run: () => void }> = [
    { icon: 'outdent', name: 'アウトデント', run: onOutdent },
    { icon: 'indent', name: 'インデント', run: onIndent },
    { icon: 'list', name: 'リスト化', run: onBullet },
    { icon: 'heading', name: '見出し（押すたびに H1→H2→H3→解除）', run: onHeading },
    { icon: 'quote', name: '引用', run: onQuote },
    { icon: 'bold', name: '太字', run: onBold },
    { icon: 'italic', name: '斜体', run: onItalic },
    { icon: 'code-inline', name: 'インラインコード', run: onInlineCode },
    { icon: 'code-block', name: 'コードブロック', run: onCodeFence },
    { icon: 'rule', name: '水平線', run: onHorizontalRule },
    { icon: 'image', name: '画像を追加', run: onImage },
  ];
  if (onRequestExtract) {
    buttons.push({ icon: 'extract', name: '選択範囲を新しいノートへ切り出す', run: onExtract });
  }

  return (
    <div
      ref={barRef}
      className={followKeyboard ? 'mm-accessory-bar' : 'mm-accessory-bar mm-accessory-bar--static'}
      role="toolbar"
      aria-label="入力補助"
    >
      {buttons.map((button) => (
        <button
          key={button.name}
          type="button"
          className="mm-accessory-bar__button"
          // IME 規律: フォーカスを奪わない。
          onPointerDown={handlePointerDown}
          onClick={button.run}
          aria-label={button.name}
          title={button.name}
        >
          <Icon name={button.icon} size={22} />
        </button>
      ))}
    </div>
  );
}

export const AccessoryBar = memo(AccessoryBarImpl);
AccessoryBar.displayName = 'AccessoryBar';

/**
 * 画像挿入のヘルパー。アップロード完了後に呼び出し側から使う。
 * IME 規律: 変換中は挿入しない（DOM 書き換えを行わない）。
 */
export function insertImageReference(
  adapter: EditorAdapter | null,
  url: string,
  alt = '',
): boolean {
  if (!adapter) return false;
  if (adapter.isComposing()) return false;
  const sel = adapter.getSelection();
  const edit = insertAtSelection(
    { value: adapter.getValue(), selectionStart: sel.start, selectionEnd: sel.end },
    `![${alt}](${url})`,
  );
  adapter.applyEdit(edit);
  return true;
}
